import { Component, OnInit, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { isEqual } from 'lodash-es';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil, tap } from 'rxjs/operators';

import {
  ActiveModeCommands,
  FeatureActionFields,
  FeatureCommands,
  FeatureNames,
  PropertiesStringValueGeneric,
  SetNumberValueCommands,
  FeaturePropertyKey,
  Feature,
  IsNumberValueProperty,
  IsActiveProperty,
  ActivateDeactivateCommands,
  DeviceIds,
} from '@vi-iot/client';

import { HemsStore, InstallationFeaturesStore, isReady, WallboxChargingModes } from '@store';

import { SUFFIX } from '../../../../shared/common/units';

@Component({
  selector: 'app-wallbox-charging-mode-overlay',
  templateUrl: './wallbox-charging-mode-overlay.component.html',
  styleUrls: ['./wallbox-charging-mode-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WallboxChargingModeOverlayComponent implements OnInit, OnDestroy {
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly SUFFIX = SUFFIX;
  chargingModes$: Observable<
    Feature<PropertiesStringValueGeneric<keyof typeof WallboxChargingModes>, ActiveModeCommands>
  >;
  chargingModesArray: string[];
  vehicleChargingStationAllowBatteryDischarge$: Observable<
    Feature<IsActiveProperty, ActivateDeactivateCommands>
  >;
  chargingLimit$: Observable<Feature<IsNumberValueProperty, SetNumberValueCommands>>;
  ngUnsubscribe$: Subject<void> = new Subject<void>();
  limits: number[];
  hemsGateway: string;

  constructor(
    private installationFeaturesStore: InstallationFeaturesStore,
    private hemsStore: HemsStore
  ) {}

  ngOnInit(): void {
    this.vehicleChargingStationAllowBatteryDischarge$ = this.installationFeaturesStore.selectInstallationHemsFeature(
      FeatureNames.vehicleChargingStationAllowBatteryDischarge
    );
    this.chargingModes$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature<
        PropertiesStringValueGeneric<WallboxChargingModes>,
        ActiveModeCommands
      >(FeatureNames.wallboxChargingMode)
      .pipe(
        filter((feature) => isReady(feature)),
        distinctUntilChanged(isEqual),
        tap((feature) => {
          this.chargingModesArray = feature?.commands?.setMode?.params?.mode?.constraints?.enum;
        })
      );

    this.chargingLimit$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature<IsNumberValueProperty, SetNumberValueCommands>(
        FeatureNames.wallboxChargingLimit
      )
      .pipe(
        filter((feature) => !!feature?.commands?.setValue?.params?.value?.constraints),
        distinctUntilChanged(isEqual),
        tap((feature) => {
          const { min, max, stepping } = feature.commands.setValue.params.value.constraints;
          this.limits = [];
          for (let i = min; i <= max; i += stepping) {
            this.limits.push(i);
          }
        })
      );
    this.hemsStore.hemsGatewaySerialIfNoError$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((gwSerial) => (this.hemsGateway = gwSerial));
  }

  onVehicleChargingStation(feature: Feature<IsActiveProperty, ActivateDeactivateCommands>): void {
    this.installationFeaturesStore.updateFeature(
      feature.feature,
      {},
      FeaturePropertyKey.active,
      null,
      feature.properties.active.value ? FeatureCommands.deactivate : FeatureCommands.activate,
      this.hemsGateway,
      DeviceIds.Hems
    );
  }
  getModeLabel(mode: string): string {
    if (mode === WallboxChargingModes.GREEN) {
      return '+INSTALLATIONS.INSTALLATION.WALLBOX.GREEN_CHARGING';
    }
    if (mode === WallboxChargingModes.MIXED) {
      return '+INSTALLATIONS.INSTALLATION.WALLBOX.MIXED_CHARGING';
    }
  }

  onChargingModeSelect(value: string): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.wallboxChargingMode,
      value,
      FeaturePropertyKey.value,
      FeatureActionFields.mode,
      FeatureCommands.setMode
    );
  }

  onChargingLimitSelect(value: string): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.wallboxChargingLimit,
      value,
      FeaturePropertyKey.value,
      FeatureActionFields.value,
      FeatureCommands.setValue
    );
  }
  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
